import { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useBusinesses } from "@/hooks/use-businesses";
import { useLocation } from "wouter";
import { ArrowLeft, Loader2, Globe } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { timezones } from "@/lib/timezones";

// Define form schema for business editing
const businessSchema = z.object({
  name: z.string().min(2, "Business name must be at least 2 characters"),
  description: z.string().optional(),
  industry: z.string().min(1, "Please select an industry"),
  logoUrl: z.string().optional(),
  timezone: z.string().optional(),
  phone: z.string().optional().or(z.literal("")),
  email: z.string().email().optional().or(z.literal("")),
  address: z.string().optional().or(z.literal("")),
  taxId: z.string().optional().or(z.literal("")),
  entityType: z.string().optional().or(z.literal("")),
});

// Industry options
const industries = [
  { value: "accounting", label: "Accounting & Finance" },
  { value: "agriculture", label: "Agriculture" },
  { value: "construction", label: "Construction" },
  { value: "consulting", label: "Consulting" },
  { value: "education", label: "Education" },
  { value: "healthcare", label: "Healthcare" },
  { value: "hospitality", label: "Hospitality & Tourism" },
  { value: "legal", label: "Legal Services" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "marketing", label: "Marketing & Advertising" },
  { value: "media", label: "Media & Entertainment" },
  { value: "nonprofit", label: "Nonprofit" },
  { value: "real_estate", label: "Real Estate" },
  { value: "retail", label: "Retail" },
  { value: "technology", label: "Technology & Software" },
  { value: "transportation", label: "Transportation & Logistics" },
  { value: "other", label: "Other" },
];

type BusinessFormValues = z.infer<typeof businessSchema>;

interface EditBusinessPageProps {
  id: string;
}

export default function EditBusinessPage({ id }: EditBusinessPageProps) {
  const { businesses, updateBusiness, isPendingUpdate } = useBusinesses();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  
  // Find the business by id
  const businessId = parseInt(id);
  const business = businesses.find(b => b.id === businessId);
  
  // Initialize form with default values
  const form = useForm<BusinessFormValues>({
    resolver: zodResolver(businessSchema),
    defaultValues: {
      name: "",
      description: "",
      industry: "",
      logoUrl: "",
      timezone: "America/New_York",
      phone: "",
      email: "",
      address: "",
      taxId: "",
      entityType: "",
    },
  });
  
  // Update form values when business data is available
  useEffect(() => {
    if (business) {
      form.reset({
        name: business.name,
        description: business.description || "",
        industry: business.industry || "",
        logoUrl: business.logoUrl || "",
        timezone: business.timezone || "America/New_York",
        phone: (business as any).phoneNumber || (business as any).phone_number || "",
        email: (business as any).email || "",
        address: (business as any).address || "",
        taxId: (business as any).taxId || (business as any).tax_id || "",
        entityType: (business as any).entityType || (business as any).entity_type || "",
      });
      setIsLoading(false);
    } else if (businesses.length > 0) {
      // If businesses are loaded but this one wasn't found
      toast({
        title: "Business not found",
        description: "The business you're trying to edit doesn't exist",
        variant: "destructive",
      });
      navigate("/settings");
    }
  }, [business, businesses, form, navigate, toast]);
  
  // Handle form submission
  const onSubmit = (values: BusinessFormValues) => {
    // Properly format the data for the update mutation
    updateBusiness({ id: businessId, ...values })
      .then(() => {
        toast({
          title: "Business updated",
          description: "Your business information has been updated successfully",
        });
        navigate("/settings");
      })
      .catch((error) => {
        toast({
          title: "Update failed",
          description: error.message || "Failed to update business information",
          variant: "destructive",
        });
      });
  };
  
  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="icon" 
              className="mr-2" 
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-neutral-900">Edit Business</h1>
          </div>
          <p className="mt-1 text-sm text-neutral-500 ml-9">
            Update your business information
          </p>
        </div>
        
        <div className="max-w-3xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
              <CardDescription>
                Edit the details about your business
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Name*</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Your Business Name" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="industry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Industry*</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select an industry" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {industries.map((industry) => (
                              <SelectItem key={industry.value} value={industry.value}>
                                {industry.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description (Optional)</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Briefly describe your business"
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          This helps us understand your business better
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="logoUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Logo URL (Optional)</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="https://example.com/logo.png" 
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription>
                          Enter a URL to your business logo
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Timezone*</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <Globe className="h-4 w-4 mr-2 text-muted-foreground" />
                              <SelectValue placeholder="Select your timezone" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="max-h-[240px]">
                            {timezones.map((timezone) => (
                              <SelectItem key={timezone.value} value={timezone.value}>
                                {timezone.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Choose the timezone for your business location
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  {/* Contact & Address */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input placeholder="(555) 123-4567" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Business Email</FormLabel>
                          <FormControl>
                            <Input placeholder="contact@business.com" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Address</FormLabel>
                        <FormControl>
                          <Input placeholder="123 Main St, Sacramento, CA 95814" {...field} />
                        </FormControl>
                        <FormDescription>Full address (used on invoices)</FormDescription>
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="taxId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>EIN / Tax ID</FormLabel>
                          <FormControl>
                            <Input placeholder="XX-XXXXXXX" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="entityType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Entity Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || ""}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select entity type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                              <SelectItem value="llc">LLC</SelectItem>
                              <SelectItem value="s_corp">S-Corporation</SelectItem>
                              <SelectItem value="c_corp">C-Corporation</SelectItem>
                              <SelectItem value="partnership">Partnership</SelectItem>
                              <SelectItem value="nonprofit">Nonprofit</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button type="button" variant="outline" onClick={() => navigate("/settings")}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isPendingUpdate}>
                      {isPendingUpdate ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
